import React from 'react'
import {
  PaymentPage,
  FilterPage,
  FramePage,
  CapturePage,
  ResultPage,
  RetakePage,
  SelectPage,
  CheckoutPage,
  WelcomePage,
  CameraPage,
  EditPage,
  ModeSelectPage
} from './index'

// Import Bahela custom pages
import * as BahelaPages from '../bahela/index'
import * as BahelakotakPages from '../bahelakotak/index'
import * as BahelaEventPages from '../bahela/event'
import * as BahelaReceiptPages from '../bahela-receipt/index'
import * as BahelaReceiptTouchPages from '../bahela-receipt/touch'
import * as BahelaQuickPages from '../bahela-quick/index'
import * as SamuPages from '../samu/index'
import * as PhotophoriaPages from '../photophoria/index'
import * as BoothDaughterPages from '../booth-daughter/index'
import * as YottaPages from '../yotta/index'

// Import Bingkai Kenangan custom pages (Malaysia; see
// docs/bingkai-kenangan-implementation-plan.md)
import * as BingkaiKenanganPages from '../bingkai-kenangan/index'

// Import Edit custom pages
import * as EditPages from '../edit/index'

// Import Newspaper custom pages
import * as NewspaperPages from '../newspaper/index'
import * as NewspaperAhboxPages from '../newspaper-ahbox/index'
import * as NewspaperKotabaruPages from '../newspaper-kotabaru/index'
import * as NewspaperTuguPages from '../newspaper-tugu/index'

// Add more custom path imports here as needed
// import * as OtherCustomPages from '../other-custom-path'

interface PageComponents {
  Welcome: React.ComponentType
  Camera: React.ComponentType
  Tutorial?: React.ComponentType
  Payment: React.ComponentType
  Filter: React.ComponentType
  Frame: React.ComponentType
  Capture: React.ComponentType
  Result: React.ComponentType
  Retake: React.ComponentType
  Select: React.ComponentType
  Edit: React.ComponentType
  Checkout: React.ComponentType
  /**
   * Hub "Pilih Mode" picker. Provided by the default set so every brand set
   * inherits it via `...defaultPages`; only registered as a route when the
   * project is a hub (View.tsx).
   */
  ModeSelect?: React.ComponentType

  Processing?: React.ComponentType
}

interface CustomRoutes {
  [key: string]: PageComponents
}

// Default pages as fallback
const defaultPages: PageComponents = {
  Welcome: WelcomePage,
  Payment: PaymentPage,
  Camera: CameraPage,
  Filter: FilterPage,
  Frame: FramePage,
  Capture: CapturePage,
  Result: ResultPage,
  Retake: RetakePage,
  Select: SelectPage,
  Edit: EditPage,
  Checkout: CheckoutPage,
  ModeSelect: ModeSelectPage
}

// Map custom paths to their respective components
const customRoutes: CustomRoutes = {
  bahela: {
    ...defaultPages, // Fallback for any missing pages
    ...BahelaPages
  },
  bahelaevent: {
    ...defaultPages, // Fallback for any missing pages
    ...BahelaEventPages
  },
  // Dual-camera wardrobe booth: Bahela-branded flow, dual-concurrent capture page.
  bahelakotak: {
    ...defaultPages,
    ...BahelaPages,
    ...BahelakotakPages
  },
  // Karcis, analog-TV edition (Mox Space, Bilik Keliling): no touchscreen, no
  // mouse, no navigation a guest can reach — the flow drives itself end to end.
  // All seven live karcis projects run this today.
  bahelastruk: {
    ...defaultPages, // Fallback for any missing pages
    ...BahelaReceiptPages
  },
  // Karcis, touchscreen edition (Cikstik iPad): the guest starts the session by
  // tapping, one payment prints `captureCount` karcis, and operator chrome is
  // PIN-gated because a finger can reach it. Same page, different profile —
  // see pages/bahela-receipt/karcisVariant.ts.
  'bahelastruk-touch': {
    ...defaultPages, // Fallback for any missing pages
    ...BahelaReceiptTouchPages
  },
  'bahela-quick': {
    ...defaultPages,
    ...BahelaQuickPages
  },
  samu: {
    ...defaultPages, // Fallback for any missing pages
    ...SamuPages
  },
  samu2o: {
    ...defaultPages, // Fallback for any missing pages
    ...SamuPages,
    Welcome: WelcomePage
  },
  photophoria: {
    ...defaultPages, // Fallback for any missing pages
    ...PhotophoriaPages
  },
  'booth-daughter': {
    ...defaultPages, // Fallback for any missing pages
    ...BoothDaughterPages
  },
  yotta: {
    ...defaultPages, // Fallback for any missing pages
    ...YottaPages
  },
  // Bingkai Kenangan: `frame` is the 4-cut/8-cut picker and `edit` is the
  // border + sticker step, so the flow runs capture -> edit -> select rather
  // than the default capture -> select -> edit. Capture, Retake and Result are
  // still the stock pages while Phase 1 stands up the flow.
  'bingkai-kenangan': {
    ...defaultPages,
    ...BingkaiKenanganPages
  },
  edit: {
    ...defaultPages, // Fallback for any missing pages
    ...EditPages
  },
  newspaper: {
    ...defaultPages, // Fallback for any missing pages
    ...NewspaperPages
  },
  'newspaper-ahbox': {
    ...defaultPages, // Fallback for any missing pages
    ...NewspaperAhboxPages
  },
  'newspaper-kotabaru': {
    ...defaultPages, // Fallback for any missing pages
    ...NewspaperKotabaruPages
  },
  'newspaper-tugu': {
    ...defaultPages, // Fallback for any missing pages
    ...NewspaperTuguPages
  }
  // Add more custom paths here
  // other-path: {
  //   ...defaultPages,
  //   ...OtherCustomPages
  // }
}

export const getCustomRoutes = (customPath: string | undefined): PageComponents => {
  if (!customPath) return defaultPages
  return customRoutes[customPath] || defaultPages
}

/** All registered custom paths — used by the Sim Mode harness path picker. */
export const CUSTOM_ROUTE_KEYS = Object.keys(customRoutes)
